// Service Outages tab — extracted from the legacy SecurityDashboard (wave 3:
// detail cards with label / value rows, Addis 12-hour times).
//
// Lazy-loaded /service_outages with admin-driven "Mark as Resolved"
// action. Only fetches when admin opens the tab. The write is unchanged:
// set(service_outages/<key>, { active: false, resolvedAt, resolvedBy }).

import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, get, set } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import SmsFailedOutlinedIcon from '@mui/icons-material/SmsFailedOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CircularLoading from '../../components/CircularLoading';
import { showToast, confirmDialog, InlineMessage, EmptyState } from '../../components/feedback';
import { DetailCard, InfoRow, StatusChip } from '../../components/modern';
import { formatAddisDateTime } from '../../common/exportUtils';
import { tokens as c } from '../../theme';

export default function ServiceOutages() {
  const { t } = useTranslation();
  const { database } = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const isAdmin = auth?.profile?.usertype === 'admin';

  const [serviceOutages, setServiceOutages] = useState({});
  const [loading, setLoading] = useState(true);
  const [clearingOutage, setClearingOutage] = useState(null);

  useEffect(() => {
    if (!database || !isAdmin) return;
    let cancelled = false;
    const fetchOutages = async () => {
      const snapshot = await get(ref(database, 'service_outages'));
      if (cancelled) return;
      setServiceOutages(snapshot.val() || {});
      setLoading(false);
    };
    fetchOutages();
    return () => {
      cancelled = true;
    };
  }, [database, isAdmin]);

  const handleClearOutage = async (serviceKey) => {
    if (!database) return;
    const ok = await confirmDialog({
      title: t('outage_resolve_confirm', { service: serviceKey.toUpperCase(), defaultValue: 'Mark the {{service}} outage as resolved?' }),
      confirmLabel: t('outage_mark_resolved', { defaultValue: 'Mark as resolved' }),
    });
    if (!ok) return;

    setClearingOutage(serviceKey);
    try {
      const outageRef = ref(database, `service_outages/${serviceKey}`);
      const resolved = {
        active: false,
        resolvedAt: Date.now(),
        resolvedBy: auth?.profile?.email || auth?.profile?.uid || 'admin',
      };
      await set(outageRef, resolved);
      setServiceOutages((prev) => ({
        ...prev,
        [serviceKey]: { ...(prev[serviceKey] || {}), ...resolved },
      }));
      showToast.success(t('outage_resolved', { defaultValue: 'Marked as resolved' }));
    } catch (error) {
      console.error('Failed to clear outage:', error);
      showToast.error(t('outage_clear_failed', { defaultValue: 'Could not clear the outage.' }));
    } finally {
      setClearingOutage(null);
    }
  };

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  if (loading) return <CircularLoading inline />;

  const entries = Object.entries(serviceOutages);
  const activeOutageCount = Object.values(serviceOutages).filter((o) => o && o.active).length;
  const code = (text, quiet) => (
    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', wordBreak: 'break-all', color: quiet ? c.TEXT_3 : c.INK }}>
      {text}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {activeOutageCount > 0 ? (
        <InlineMessage kind="error">
          {t('outages_active_detected', {
            count: activeOutageCount,
            defaultValue_one: '{{count}} active service outage detected. Review and resolve below.',
            defaultValue_other: '{{count}} active service outages detected. Review and resolve below.',
            defaultValue: '{{count}} active service outages detected. Review and resolve below.',
          })}
        </InlineMessage>
      ) : (
        <InlineMessage kind="success">{t('outages_all_ok', { defaultValue: 'All external services are operating normally.' })}</InlineMessage>
      )}

      {entries.length === 0 ? (
        <Paper sx={{ p: 2 }}>
          <EmptyState icon={<CloudDoneOutlinedIcon />} title={t('outages_none', { defaultValue: 'No outage records found.' })} />
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
          {entries.map(([key, outage]) => (
            <DetailCard
              key={key}
              icon={outage.active ? <ReportProblemOutlinedIcon /> : <SmsFailedOutlinedIcon />}
              hue={outage.active ? 'danger' : 'success'}
              title={t('outages_service', { service: key.toUpperCase(), defaultValue: '{{service}} Service' })}
              action={
                outage.active ? (
                  <StatusChip tone="error" icon={<ReportProblemOutlinedIcon />} label={t('outages_status_outage', { defaultValue: 'Outage' })} />
                ) : (
                  <StatusChip tone="success" icon={<CheckCircleOutlineRoundedIcon />} label={t('outages_status_resolved', { defaultValue: 'Resolved' })} />
                )
              }
              sx={outage.active ? { borderColor: c.DANGER, boxShadow: `inset 0 0 0 1px ${c.DANGER}` } : undefined}
              list={false}
            >
              <Box component="dl" sx={{ m: 0 }}>
              {outage.error ? <InfoRow label={t('error', { defaultValue: 'Error' })} alignTop>{code(outage.error)}</InfoRow> : null}
              {outage.type ? <InfoRow label={t('type', { defaultValue: 'Type' })}>{outage.type}</InfoRow> : null}
              {outage.httpStatus ? <InfoRow label={t('outages_http_status', { defaultValue: 'HTTP Status' })}>{outage.httpStatus}</InfoRow> : null}
              {outage.lastErrorAt ? <InfoRow label={t('outages_last_error', { defaultValue: 'Last Error' })}>{formatAddisDateTime(outage.lastErrorAt)}</InfoRow> : null}
              {outage.startedAt ? <InfoRow label={t('outages_started', { defaultValue: 'Started' })}>{formatAddisDateTime(outage.startedAt)}</InfoRow> : null}
              {outage.resolvedAt ? <InfoRow label={t('outages_status_resolved', { defaultValue: 'Resolved' })}>{formatAddisDateTime(outage.resolvedAt)}</InfoRow> : null}
              {outage.resolvedBy ? (
                <InfoRow label={t('outages_resolved_by', { defaultValue: 'Resolved By' })}>
                  {outage.resolvedBy === 'auto_resolved' ? t('outages_auto_resolved', { defaultValue: 'Auto-resolved (successful SMS)' }) : outage.resolvedBy}
                </InfoRow>
              ) : null}
              {outage.previousError && !outage.active ? (
                <InfoRow label={t('outages_previous_error', { defaultValue: 'Previous Error' })} alignTop>
                  {code(outage.previousError, true)}
                </InfoRow>
              ) : null}
              {outage.previousType && !outage.active ? (
                <InfoRow label={t('outages_previous_type', { defaultValue: 'Previous Error Type' })}>{outage.previousType}</InfoRow>
              ) : null}
              </Box>
              {outage.active ? (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    startIcon={clearingOutage === key ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineRoundedIcon />}
                    disabled={clearingOutage === key}
                    onClick={() => handleClearOutage(key)}
                  >
                    {clearingOutage === key ? t('outage_clearing', { defaultValue: 'Clearing...' }) : t('outage_mark_resolved', { defaultValue: 'Mark as resolved' })}
                  </Button>
                </Box>
              ) : null}
            </DetailCard>
          ))}
        </Box>
      )}
    </Box>
  );
}
